import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import { Box } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import type { PartnerMediaFileListItemDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { MediaFilePlayer } from "MusicAudio/Components/MediaFilePlayer";
import { ActionsCell } from "Shared/DataTable/ActionsCell";
import { formatDate } from "Shared/Utils/Format/date";

const columnHelper = createColumnHelper<PartnerMediaFileListItemDto>();

export const MEDIA_FILES_SORTABLE_COLUMNS = [
  "displayName",
  "fileName",
  "durationSeconds",
  "dateCreated",
] as const;

const isSortable = (id: string) =>
  (MEDIA_FILES_SORTABLE_COLUMNS as readonly string[]).includes(id);

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "-";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function useMediaFilesTableColumns(
  onDownload: (file: PartnerMediaFileListItemDto) => void,
  onDeleteRequest: (file: PartnerMediaFileListItemDto) => void,
) {
  const { t } = useResource();

  return useMemo(
    () => [
      columnHelper.display({
        id: "player",
        header: "",
        enableSorting: false,
        meta: { maxWidth: 40 },
        cell: (info) => <MediaFilePlayer fileID={info.row.original.fileID} />,
      }),
      columnHelper.accessor("displayName", {
        header: t(Resources.MusicAudio.DisplayName),
        enableSorting: isSortable("displayName"),
        cell: (info) => <Box>{info.getValue() ?? ""}</Box>,
      }),
      columnHelper.accessor("fileName", {
        header: t(Resources.MusicAudio.FileName),
        enableSorting: isSortable("fileName"),
        cell: (info) => <Box>{info.getValue()}</Box>,
      }),
      columnHelper.accessor("durationSeconds", {
        header: t(Resources.MusicAudio.Duration),
        enableSorting: isSortable("durationSeconds"),
        cell: (info) => <Box>{formatDuration(info.getValue())}</Box>,
      }),
      columnHelper.accessor("dateCreated", {
        header: t(Resources.MusicAudio.DateCreated),
        enableSorting: isSortable("dateCreated"),
        cell: (info) => <Box>{formatDate(info.getValue())}</Box>,
      }),
      columnHelper.display({
        id: "action",
        header: "",
        enableSorting: false,
        meta: { maxWidth: 50, justifySelf: "end" },
        cell: (info) => (
          <ActionsCell
            row={info.row.original}
            items={[
              {
                label: t(Resources.MusicAudio.Download),
                icon: <DownloadIcon />,
                onClick: onDownload,
              },
              {
                label: t(Resources.Common.Delete),
                icon: <DeleteIcon />,
                color: "error",
                onClick: onDeleteRequest,
              },
            ]}
          />
        ),
      }),
    ],
    [t, onDownload, onDeleteRequest],
  );
}
